import { confirmed as passwordChangedNotice } from '../../../../shared/password-change';
import React from 'react';
import { connect } from 'react-redux';
import { translate } from '../../base/i18n/functions';
import SettingsButton from '../../settings/components/web/SettingsButton';
import { SETTINGS_TABS } from '../../settings/constants';
import CertifiedIpCtsNotice from '../../base/certified-mode/components/CertifiedIpCtsNotice.web';
import { AbstractWelcomePage, _mapStateToProps } from './AbstractWelcomePage';
import AdminTwoStepPanel from './AdminTwoStepPanel.web';
import { consumePendingRoomRedirect, getPersistentItem, peekPersistentItem, removePersistentItem, setPersistentItem } from '../../vrs-auth/storage';
import { releaseBrowserMediaDevices } from '../../vrs-layout/mediaCleanup.web';
import { PROVIDER_DESTINATIONS, destinationHref, planProviderSignIn, providerDestinations, providerNextTarget as nextTargetFrom, readHandoffFragment, roleChoiceLabel } from '../providerSignIn';
import { getAppName, getLogoUrl, getLogoWhiteUrl, getWhitelabelConfig } from '../../base/whitelabel/functions';
const PROVIDER_COPY = {
    providerHome: 'Provider home',
    signIn: 'Sign in',
    signOut: 'Sign out'
};
/** The page a signed-out visit came from, with its query and hash, if it is a provider destination. */
function providerNextTarget() {
    return nextTargetFrom(window.location.search, Object.values(PROVIDER_DESTINATIONS));
}
function preferredProviderDestination() {
    const next = providerNextTarget();
    if (next) {
        return next.split(/[?#]/)[0];
    }
    if (window.location.hostname.toLowerCase().startsWith('jobs.')) {
        return PROVIDER_DESTINATIONS.jobs;
    }
    return PROVIDER_DESTINATIONS.queue;
}
/**
 * A stored credential this page may treat as a live session. Sign-out in any
 * tab clears localStorage, and getPersistentItem would copy this tab's
 * sessionStorage copy back for every tab, undoing it.
 */
function storedCredential(key) {
    try {
        if (window.localStorage.getItem(key) === null) {
            return null;
        }
    }
    catch {
        // No readable localStorage (a private window): this tab's copy is all there is.
    }
    return peekPersistentItem(key);
}
function storedProviderUser() {
    try {
        const role = peekPersistentItem('vrs_user_role');
        const user = JSON.parse(peekPersistentItem('vrs_user_info') || 'null');
        if ((role === 'interpreter' || role === 'captioner') && user?.role === role
            && storedCredential('vrs_auth_token')) {
            return user;
        }
        const adminRole = peekPersistentItem('vrs_admin_role');
        if ((adminRole === 'admin' || adminRole === 'superadmin') && storedCredential('vrs_admin_token')) {
            return {
                email: peekPersistentItem('vrs_admin_email') || undefined,
                name: peekPersistentItem('vrs_admin_name') || undefined,
                role: adminRole,
                tenantId: peekPersistentItem('vrs_admin_tenant')
            };
        }
    }
    catch {
        // Damaged storage is a signed-out view, never an inferred identity.
    }
    return null;
}
/**
 * The Web container rendering the welcome page: one provider credential form,
 * followed by the destinations that the authenticated account may enter.
 */
class WelcomePage extends AbstractWelcomePage {
    constructor(props) {
        super(props);
        this.state = {
            ...this.state,
            generateRoomNames: interfaceConfig.GENERATE_ROOMNAMES_ON_WELCOME_PAGE,
            recentMeetingsCollapsed: true,
            providerUser: storedProviderUser(),
            email: '',
            password: '',
            showPassword: false,
            isSubmitting: false,
            // Set once a password has been accepted and a second factor is
            // outstanding. The challenge id stands in for the password, which
            // is therefore never held for a second request.
            challengeId: '',
            phoneHint: '',
            twoFactorCode: '',
            // The same field takes a recovery code; this only relabels it.
            recoveryCode: false,
            // 'totp': the challenge is the manager's authenticator code on the
            // operations server; '' is vrs-server's SMS code (providers).
            secondFactor: '',
            // A manager who must set up two-step sign-in before the session.
            enrollmentTicket: '',
            // A signed-in manager opened Two-step sign-in.
            showTwoStep: false,
            // Set when the password matches more than one role of one person
            // (vrs-ops-server/src/linked-roles.ts). The person chooses one.
            roleChoices: [],
            error: ''
        };
        this._titleHasNotAllowCharacter = false;
        this._additionalContentRef = null;
        this._roomInputRef = null;
        this._additionalToolbarContentRef = null;
        this._additionalCardRef = null;
        this._emailRef = null;
        this._passwordRef = null;
        this._additionalCardTemplate = document.getElementById('welcome-page-additional-card-template');
        this._additionalContentTemplate = document.getElementById('welcome-page-additional-content-template');
        this._additionalToolbarContentTemplate = document.getElementById('settings-toolbar-additional-content-template');
        this._onFormSubmit = this._onFormSubmit.bind(this);
        this._onRoomChange = this._onRoomChange.bind(this);
        this._setAdditionalCardRef = this._setAdditionalCardRef.bind(this);
        this._setAdditionalContentRef = this._setAdditionalContentRef.bind(this);
        this._setRoomInputRef = this._setRoomInputRef.bind(this);
        this._setAdditionalToolbarContentRef = this._setAdditionalToolbarContentRef.bind(this);
        this._renderFooter = this._renderFooter.bind(this);
        this._handleAuthSubmit = this._handleAuthSubmit.bind(this);
        this._handleEmailChange = this._handleEmailChange.bind(this);
        this._handlePasswordChange = this._handlePasswordChange.bind(this);
        this._togglePasswordVisibility = this._togglePasswordVisibility.bind(this);
        this._handleTwoFactorCodeChange = this._handleTwoFactorCodeChange.bind(this);
        this._handleTwoFactorSubmit = this._handleTwoFactorSubmit.bind(this);
        this._goToPasswordReset = this._goToPasswordReset.bind(this);
        this._signOutProvider = this._signOutProvider.bind(this);
        this._clearRoleChoice = this._clearRoleChoice.bind(this);
    }
    componentDidMount() {
        super.componentDidMount();
        releaseBrowserMediaDevices();
        document.body.classList.add('welcome-page');
        document.title = interfaceConfig.APP_NAME;
        if (this.state.generateRoomNames) {
            this._updateRoomName();
        }
        if (this._shouldShowAdditionalContent()) {
            this._additionalContentRef?.appendChild(this._additionalContentTemplate?.content.cloneNode(true));
        }
        if (this._shouldShowAdditionalToolbarContent()) {
            this._additionalToolbarContentRef?.appendChild(this._additionalToolbarContentTemplate?.content.cloneNode(true));
        }
        if (this._shouldShowAdditionalCard()) {
            this._additionalCardRef?.appendChild(this._additionalCardTemplate?.content.cloneNode(true));
        }
        this._applyTheme();
        void this._redeemHandoff();
        this._openTwoStepFromLink();
    }
    /**
     * The admin dashboard's account menu links here (#two-step) to open
     * Two-step sign-in for the signed-in manager.
     */
    _openTwoStepFromLink() {
        const { providerUser } = this.state;
        if (window.location.hash !== '#two-step') {
            return;
        }
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
        if (providerUser?.role === 'admin' || providerUser?.role === 'superadmin') {
            this.setState({ showTwoStep: true });
        }
    }
    /**
     * The Terp tab of MalkaVRS opens this page with a one-time code
     * (readHandoffFragment). The fragment goes from the address at once, so
     * no history entry keeps it. The code is used like a password: it gives
     * the linked interpreter's session, or that account's second-factor
     * challenge, which the form below then asks for.
     */
    async _redeemHandoff() {
        const handoff = readHandoffFragment(window.location.hash);
        if (!handoff) {
            return;
        }
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
        this.setState({ isSubmitting: true, error: '' });
        try {
            const res = await fetch(`${this._getApiBase()}/api/auth/handoff/redeem`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: handoff.code, codeVerifier: handoff.verifier })
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.twoFactorRequired) {
                this.setState({
                    challengeId: data.challengeId,
                    isSubmitting: false,
                    phoneHint: data.phoneHint || '',
                    secondFactor: '',
                    twoFactorCode: '',
                    error: ''
                });
                return;
            }
            if (res.ok) {
                this._completeSignIn(data, 'interpreter');
                return;
            }
            this.setState({
                error: data.error || 'This sign-in link expired. Open Terp from MalkaVRS again, or sign in.',
                isSubmitting: false
            });
        }
        catch {
            this.setState({ error: 'No connection. Try again.', isSubmitting: false });
        }
    }
    componentDidUpdate(prevProps, prevState) {
        if (prevState.providerUser?.role !== this.state.providerUser?.role) {
            this._applyTheme();
        }
    }
    componentWillUnmount() {
        super.componentWillUnmount();
        document.body.classList.remove('welcome-page');
        document.body.style.removeProperty('--vrs-bg');
        document.body.style.removeProperty('--vrs-accent');
    }
    /**
     * Apply background and accent theme based on the signed-in provider.
     */
    _applyTheme() {
        const role = this.state.providerUser?.role;
        if (role !== 'captioner') {
            document.body.style.setProperty('--vrs-bg', 'linear-gradient(135deg, #0f1724 0%, #1a2332 50%, #252f3f 100%)');
            document.body.style.setProperty('--vrs-accent', '#7eb8da');
        }
        else {
            document.body.style.setProperty('--vrs-bg', 'linear-gradient(135deg, #2b1b0f 0%, #5a2f14 48%, #9a631c 100%)');
            document.body.style.setProperty('--vrs-accent', '#f2b94b');
        }
    }
    _handleEmailChange(e) {
        this.setState({ email: e.target.value, error: '', roleChoices: [] });
    }
    _handlePasswordChange(e) {
        this.setState({ password: e.target.value, error: '', roleChoices: [] });
    }
    _clearRoleChoice() {
        this.setState({ error: '', roleChoices: [] });
    }
    _handleTwoFactorCodeChange(e) {
        this.setState({ twoFactorCode: e.target.value, error: '' });
    }
    /**
     * Completes a sign-in that stopped at the second factor.
     */
    async _handleTwoFactorSubmit(e) {
        e.preventDefault();
        const { challengeId, secondFactor, twoFactorCode } = this.state;
        if (!twoFactorCode) {
            this.setState({ error: secondFactor === 'totp' ? 'Enter the code.' : 'Enter the code we sent you.' });
            return;
        }
        this.setState({ isSubmitting: true, error: '' });
        try {
            // A manager's authenticator code goes to the operations server,
            // a provider's SMS code to vrs-server. Both answer as a sign-in.
            const challengeUrl = secondFactor === 'totp'
                ? `${this._getOpsAuthBase()}/totp/challenge`
                : `${this._getApiBase()}/api/auth/2fa/challenge`;
            const res = await fetch(challengeUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ challengeId, code: twoFactorCode })
            });
            const data = await res.json();
            if (!res.ok) {
                this.setState({
                    error: data.error || 'That code is not valid.',
                    isSubmitting: false
                });
                return;
            }
            this._completeSignIn(data, data.user?.role);
        }
        catch (err) {
            this.setState({
                error: 'No connection. Try again.',
                isSubmitting: false
            });
        }
    }
    _togglePasswordVisibility() {
        this.setState((prev) => ({
            showPassword: !prev.showPassword
        }));
    }
    /**
     * Password reset is not a client flow. This used to send an interpreter or
     * a captioner to /client-profile.html, which cannot complete a reset for a
     * staff account — so "Forgot password?" led somewhere that could not help.
     */
    _goToPasswordReset() {
        const params = new URLSearchParams();
        const email = String(this.state.email || '').trim();
        if (email.includes('@')) {
            params.set('email', email);
        }
        const query = params.toString();
        window.location.href = `/reset-password.html${query ? `?${query}` : ''}`;
    }
    /**
     * Handle sign-in or registration form submission.
     */
    async _handleAuthSubmit(e) {
        e.preventDefault();
        await this._signIn();
    }
    /**
     * Signs in. `choice` is the role the person chose when the password
     * matched more than one of their roles: the directory then answers for
     * that role only, so one token has one role.
     */
    async _signIn(choice) {
        const { email, password } = this.state;
        if (!email || !password) {
            this.setState({ error: 'Email and password are required.' });
            return;
        }
        this.setState({ isSubmitting: true, error: '' });
        // Staff accounts are created by administrators, so this portal only
        // ever signs in. Client self-registration lives on the client portal.
        const apiBase = this._getApiBase();
        try {
            let directoryResponse = null;
            let directoryData = {};
            try {
                directoryResponse = await fetch(`${this._getOpsAuthBase()}/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        audience: 'provider-home',
                        identifier: email,
                        password,
                        tenantId: choice?.tenantId || getWhitelabelConfig()?.tenantId || 'malka',
                        ...choice ? { role: choice.role } : {}
                    })
                });
                directoryData = await directoryResponse.json().catch(() => ({}));
            }
            catch {
                // The directory being unreachable must not lock providers out.
                directoryResponse = null;
            }
            // A directory 401 or outage falls through to vrs-server's own
            // provider sign-in (providerSignIn.ts has the rules).
            const plan = planProviderSignIn(directoryResponse ? directoryResponse.status : null, directoryData);
            if (plan.kind === 'choose') {
                this.setState({ isSubmitting: false, roleChoices: plan.roles });
                return;
            }
            if (plan.kind === 'totp') {
                this.setState({
                    challengeId: plan.challengeId,
                    isSubmitting: false,
                    phoneHint: '',
                    recoveryCode: false,
                    roleChoices: [],
                    secondFactor: 'totp',
                    twoFactorCode: '',
                    error: ''
                });
                return;
            }
            if (plan.kind === 'totp-enroll') {
                this.setState({ enrollmentTicket: plan.ticket, isSubmitting: false, roleChoices: [], error: '' });
                return;
            }
            if (plan.kind === 'manager') {
                this._completeSignIn(directoryData, directoryData.user?.role);
                return;
            }
            if (plan.kind === 'refused') {
                this.setState({ error: plan.error, isSubmitting: false });
                return;
            }
            let refusal = plan.error;
            for (const providerRole of plan.roles) {
                const res = await fetch(`${apiBase}/api/auth/${providerRole}/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, password })
                });
                const data = await res.json().catch(() => ({}));
                // A correct password is not a session when the account carries a
                // second factor: the server sends a code and hands back a
                // challenge instead of tokens.
                if (res.ok && data.twoFactorRequired) {
                    this.setState({
                        challengeId: data.challengeId,
                        isSubmitting: false,
                        phoneHint: data.phoneHint || '',
                        secondFactor: '',
                        twoFactorCode: '',
                        error: ''
                    });
                    return;
                }
                if (res.ok) {
                    this._completeSignIn(data, providerRole);
                    return;
                }
                refusal = data.error || refusal;
                // Wrong credentials for this role: the account may be the other one.
                if (res.status !== 401) {
                    break;
                }
            }
            this.setState({
                error: refusal || 'Authentication failed.',
                isSubmitting: false
            });
        }
        catch (err) {
            this.setState({
                error: 'No connection. Try again.',
                isSubmitting: false
            });
        }
    }
    /**
     * Stores the session and leaves the portal.
     *
     * Shared by the plain password path and the two-factor path, so a session
     * established by either is stored identically — including the refresh
     * token, without which the profile surfaces sign the user out at the first
     * access-token expiry.
     */
    _completeSignIn(data, role) {
        if (!data?.token || !data?.user || !['admin', 'captioner', 'interpreter', 'superadmin'].includes(role)) {
            this.setState({ error: 'Sign-in did not return a provider session.', isSubmitting: false });
            return;
        }
        if (role === 'admin' || role === 'superadmin') {
            ['vrs_auth_token', 'vrs_refresh_token', 'vrs_user_info', 'vrs_user_role',
                'vrs_client_auth', 'vrs_interpreter_auth'].forEach(removePersistentItem);
            setPersistentItem('vrs_admin_token', data.token);
            setPersistentItem('vrs_admin_name', data.user.name || '');
            setPersistentItem('vrs_admin_email', data.user.email || '');
            setPersistentItem('vrs_admin_role', role);
            setPersistentItem('vrs_admin_tenant', data.user.tenantId || 'malka');
            // The admin dashboard reads these before it validates the session
            // (vrs-admin.html writes the same keys).
            setPersistentItem('vrs_admin_permissions', JSON.stringify(data.user.permissions || []));
            setPersistentItem('vrs_admin_billing_account', data.user.billingAccountId || '');
            setPersistentItem('vrs_admin_billing_title', data.user.billingTitle || '');
            setPersistentItem('jobs_role', 'manager');
        }
        else {
            ['vrs_admin_token', 'vrs_admin_name', 'vrs_admin_email', 'vrs_admin_role',
                'vrs_admin_tenant', 'vrs_admin_permissions', 'vrs_admin_billing_account',
                'vrs_admin_billing_title'].forEach(removePersistentItem);
            setPersistentItem('vrs_auth_token', JSON.stringify({
                token: data.token,
                userId: data.user?.id,
                name: data.user?.name
            }));
            if (data.refreshToken) {
                setPersistentItem('vrs_refresh_token', data.refreshToken);
            }
            setPersistentItem('vrs_user_info', JSON.stringify(data.user));
            setPersistentItem('vrs_user_role', role);
            setPersistentItem('jobs_role', 'interpreter');
        }
        const pendingRoomRedirect = consumePendingRoomRedirect();
        if (pendingRoomRedirect) {
            window.location.href = pendingRoomRedirect;
            return;
        }
        this.setState({
            challengeId: '',
            enrollmentTicket: '',
            error: '',
            isSubmitting: false,
            password: '',
            providerUser: data.user,
            roleChoices: [],
            secondFactor: ''
        });
    }
    async _signOutProvider() {
        const refreshToken = getPersistentItem('vrs_refresh_token');
        if (refreshToken) {
            try {
                await fetch(`${this._getApiBase()}/api/auth/logout`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refreshToken })
                });
            }
            catch {
                // Destination pages retain their stronger retryable sign-out flow.
            }
        }
        ['vrs_auth_token', 'vrs_refresh_token', 'vrs_user_info', 'vrs_user_role',
            'vrs_client_auth', 'vrs_interpreter_auth', 'vrs_admin_token', 'vrs_admin_name',
            'vrs_admin_email', 'vrs_admin_role', 'vrs_admin_tenant', 'vrs_admin_permissions',
            'vrs_admin_billing_account', 'vrs_admin_billing_title', 'jobs_role']
            .forEach(removePersistentItem);
        this.setState({ providerUser: null });
    }
    _getOpsAuthBase() {
        if (typeof config !== 'undefined' && config.vrs?.authEndpoint) {
            return config.vrs.authEndpoint;
        }
        return `${window.location.origin}/ops/api/auth`;
    }
    _getApiBase() {
        if (typeof config !== 'undefined' && config.vrs?.queueServiceUrl) {
            const wsUrl = config.vrs.queueServiceUrl;
            return wsUrl.replace(/^ws/, 'http').replace(/\/ws$/, '');
        }
        if (typeof window !== 'undefined') {
            return window.location.origin;
        }
        return 'http://localhost:3001';
    }
    render() {
        const { t } = this.props;
        const { DEFAULT_WELCOME_PAGE_LOGO_URL, DISPLAY_WELCOME_FOOTER } = interfaceConfig;
        const showAdditionalCard = this._shouldShowAdditionalCard();
        const showAdditionalContent = this._shouldShowAdditionalContent();
        const showAdditionalToolbarContent = this._shouldShowAdditionalToolbarContent();
        const contentClassName = showAdditionalContent ? 'with-content' : 'without-content';
        const footerClassName = DISPLAY_WELCOME_FOOTER ? 'with-footer' : 'without-footer';
        const { providerUser, email, password, showPassword, isSubmitting, error } = this.state;
        const { challengeId, enrollmentTicket, phoneHint, recoveryCode, roleChoices, secondFactor, showTwoStep, twoFactorCode } = this.state;
        const isManager = providerUser?.role === 'admin' || providerUser?.role === 'superadmin';
        const awaitingSecondFactor = Boolean(challengeId);
        const choices = Array.isArray(roleChoices) ? roleChoices : [];
        const isInterpreter = providerUser?.role !== 'captioner';
        const themeClass = isInterpreter ? 'moon' : 'sun';
        const tenantId = getWhitelabelConfig()?.tenantId || 'malka';
        const tenantClass = `tenant-${tenantId}`;
        // The logo of the tenant config for every tenant: one Maple logo on
        // every web page (shared/web-brand.js uses the same one).
        const logoUrl = isInterpreter ? getLogoWhiteUrl() : getLogoUrl();
        // Rendered into a visually-hidden h1: the page needs a heading in its
        // outline, but not a second label on screen under a tab that says it.
        const authTitle = providerUser ? PROVIDER_COPY.providerHome : PROVIDER_COPY.signIn;
        const preferredDestination = preferredProviderDestination();
        const nextTarget = providerNextTarget();
        // The page a signed-out visit asked for comes first, then this host's.
        const rank = (destination) => (nextTarget && destinationHref(destination, nextTarget) === nextTarget ? 2 : 0)
            + Number(destination.href === preferredDestination);
        const destinations = providerUser
            ? providerDestinations(providerUser).sort((left, right) => rank(right) - rank(left)) : [];
        return (React.createElement("div", { className: `welcome vrs-auth-page ${contentClassName} ${footerClassName}`, id: 'welcome_page' },
            React.createElement("div", { className: `vrs-auth-fullscreen ${themeClass} ${tenantClass}` },
                React.createElement("div", { "aria-hidden": 'true', className: 'vrs-celestial-background' },
                    React.createElement("div", { className: 'vrs-starfield vrs-starfield--near' }),
                    React.createElement("div", { className: 'vrs-starfield vrs-starfield--far' }),
                    React.createElement("div", { className: 'vrs-orbit-ring vrs-orbit-ring--wide' }),
                    React.createElement("div", { className: 'vrs-orbit-ring vrs-orbit-ring--tight' }),
                    React.createElement("div", { className: 'vrs-auth-planet vrs-auth-planet--earth' },
                        React.createElement("span", { className: 'vrs-auth-planet-shade' })),
                    React.createElement("div", { className: 'vrs-auth-planet vrs-auth-planet--moon' },
                        React.createElement("span", { className: 'vrs-auth-crater vrs-auth-crater--one' }),
                        React.createElement("span", { className: 'vrs-auth-crater vrs-auth-crater--two' }),
                        React.createElement("span", { className: 'vrs-auth-crater vrs-auth-crater--three' })),
                    React.createElement("div", { className: 'vrs-auth-planet vrs-auth-planet--sun' })),
                providerUser ? (React.createElement("div", { className: 'welcome-page-settings' },
                    React.createElement(SettingsButton, { defaultTab: SETTINGS_TABS.CALENDAR, isDisplayedOnWelcomePage: true }),
                    showAdditionalToolbarContent
                        ? React.createElement("div", { className: 'settings-toolbar-content', ref: this._setAdditionalToolbarContentRef })
                        : null)) : null,
                React.createElement("div", { className: 'vrs-auth-card' },
                    React.createElement("div", { className: 'vrs-auth-logo' },
                        React.createElement("img", { alt: getAppName(), className: 'vrs-auth-logo-img', src: logoUrl })),
                    React.createElement("h1", { className: 'vrs-auth-title vrs-auth-title--visually-hidden' }, authTitle),
                    error && (React.createElement("div", { className: 'vrs-auth-error' }, error)),
                    providerUser && showTwoStep ? (React.createElement(AdminTwoStepPanel, { adminToken: getPersistentItem('vrs_admin_token') || '', base: `${this._getOpsAuthBase()}/totp`, onClose: () => this.setState({ showTwoStep: false }), themeClass: themeClass })) : providerUser ? (React.createElement("div", { className: 'provider-home' },
                        React.createElement("div", { className: 'provider-home__links' }, destinations.map(destination => (React.createElement("a", { className: `provider-home__link ${themeClass}`, href: destinationHref(destination, nextTarget), key: destination.id }, destination.label)))),
                        isManager ? (React.createElement("button", { className: 'provider-home__signout', onClick: () => this.setState({ showTwoStep: true, error: '' }), type: 'button' }, "Two-step sign-in")) : null,
                        React.createElement("button", { className: 'provider-home__signout', onClick: this._signOutProvider, type: 'button' }, PROVIDER_COPY.signOut))) : enrollmentTicket ? (React.createElement(AdminTwoStepPanel, { base: `${this._getOpsAuthBase()}/totp`, onClose: () => this.setState({ enrollmentTicket: '' }), onSession: (data) => this._completeSignIn(data, data?.user?.role), themeClass: themeClass, ticket: enrollmentTicket })) : (React.createElement(React.Fragment, null,
                        awaitingSecondFactor ? (React.createElement("form", { className: 'vrs-auth-form', onSubmit: this._handleTwoFactorSubmit },
                            React.createElement("div", { className: 'vrs-auth-field' },
                                React.createElement("label", { htmlFor: 'vrs-2fa-code' }, recoveryCode
                                    ? 'Recovery code'
                                    : secondFactor === 'totp'
                                        ? 'Authenticator code'
                                        : phoneHint
                                            ? `Code sent to ${phoneHint}`
                                            : 'Verification code'),
                                React.createElement("input", { autoComplete: 'one-time-code', autoFocus: true, disabled: isSubmitting, id: 'vrs-2fa-code', inputMode: recoveryCode ? 'text' : 'numeric', onChange: this._handleTwoFactorCodeChange, placeholder: recoveryCode ? '' : '000000', required: true, type: 'text', value: twoFactorCode })),
                            React.createElement("button", { className: `vrs-auth-submit ${themeClass}`, disabled: isSubmitting, type: 'submit' }, isSubmitting ? 'Verifying...' : 'Verify'),
                            recoveryCode ? null : (React.createElement("div", { className: 'vrs-auth-switch' },
                                React.createElement("button", { onClick: () => this.setState({ recoveryCode: true }), type: 'button' }, "Use a recovery code"))),
                            React.createElement("button", { className: 'vrs-password-toggle', disabled: isSubmitting, onClick: () => this.setState({
                                    challengeId: '', error: '', recoveryCode: false, secondFactor: '', twoFactorCode: ''
                                }), type: 'button' }, "Back"))) : choices.length ? (React.createElement("div", { "aria-labelledby": 'vrs-role-choice-label', className: 'vrs-auth-form', role: 'group' },
                            React.createElement("p", { className: 'vrs-auth-subtitle', id: 'vrs-role-choice-label' }, "Continue as"),
                            choices.map(choice => (React.createElement("button", { className: `vrs-auth-submit ${themeClass}`, disabled: isSubmitting, key: `${choice.role}:${choice.tenantId || 'platform'}`, onClick: () => this._signIn(choice), type: 'button' }, roleChoiceLabel(choice, choices)))),
                            React.createElement("button", { className: 'vrs-password-toggle', disabled: isSubmitting, onClick: this._clearRoleChoice, type: 'button' }, "Back"))) : (React.createElement("form", { className: 'vrs-auth-form', onSubmit: this._handleAuthSubmit },
                            new URLSearchParams(window.location.search).get('password-changed') === '1' && (React.createElement("p", { className: 'vrs-auth-subtitle', role: 'status' }, passwordChangedNotice)),
                            React.createElement("div", { className: 'vrs-auth-field' },
                                React.createElement("label", { htmlFor: 'vrs-email' }, "Email or username"),
                                React.createElement("input", { autoComplete: 'username', disabled: isSubmitting, id: 'vrs-email', onChange: this._handleEmailChange, placeholder: 'Email or username', required: true, type: 'text', value: email })),
                            React.createElement("div", { className: 'vrs-auth-field' },
                                React.createElement("label", { htmlFor: 'vrs-password' }, "Password"),
                                React.createElement("div", { className: 'vrs-password-control' },
                                    React.createElement("input", { autoComplete: 'current-password', disabled: isSubmitting, id: 'vrs-password', onChange: this._handlePasswordChange, placeholder: '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022', required: true, type: showPassword ? 'text' : 'password', value: password }),
                                    React.createElement("button", { "aria-label": showPassword ? 'Hide password' : 'Show password', "aria-pressed": showPassword, className: 'vrs-password-toggle', disabled: isSubmitting, onClick: this._togglePasswordVisibility, type: 'button' }, showPassword ? 'Hide' : 'Show'))),
                            React.createElement("button", { className: `vrs-auth-submit ${themeClass}`, disabled: isSubmitting, type: 'submit' }, isSubmitting ? 'Signing in...' : 'Sign In'))),
                        React.createElement("div", { className: 'vrs-auth-switch' },
                            React.createElement("button", { onClick: this._goToPasswordReset, type: 'button' }, "Forgot password?")))),
                    React.createElement(CertifiedIpCtsNotice, null))),
            React.createElement("div", { style: { display: 'none' } },
                React.createElement("div", { className: 'welcome-page-content', ref: this._setAdditionalContentRef }),
                React.createElement("div", { className: 'welcome-card welcome-card--dark', ref: this._setAdditionalCardRef })),
            DISPLAY_WELCOME_FOOTER && this._renderFooter()));
    }
    /**
     * Prevents submission of the form and delegates join logic.
     */
    _onFormSubmit(event) {
        event.preventDefault();
        if (!this._roomInputRef || this._roomInputRef.reportValidity()) {
            this._onJoin();
        }
    }
    // @ts-ignore
    _onRoomChange(event) {
        const specialCharacters = ['?', '&', ':', '\'', '"', '%', '#', '.'];
        this._titleHasNotAllowCharacter = specialCharacters.some(char => event.target.value.includes(char));
        super._onRoomChange(event.target.value);
    }
    _renderFooter() {
        const { t } = this.props;
        return (React.createElement("footer", { className: 'welcome-footer' },
            React.createElement("div", { className: 'welcome-footer-centered' },
                React.createElement("div", { className: 'welcome-footer-padded' },
                    React.createElement("div", { className: 'welcome-footer-row-block welcome-footer--row-1' },
                        React.createElement("div", { className: 'welcome-footer-row-1-text' }, t('welcomepage.jitsiOnMobile')),
                        React.createElement("a", { className: 'welcome-badge', href: '#', onClick: e => e.preventDefault() },
                            React.createElement("img", { alt: t('welcomepage.mobileDownLoadLinkIos'), src: './images/app-store-badge.png' })),
                        React.createElement("a", { className: 'welcome-badge', href: '#', onClick: e => e.preventDefault() },
                            React.createElement("img", { alt: t('welcomepage.mobileDownLoadLinkAndroid'), src: './images/google-play-badge.png' })))))));
    }
    _setAdditionalCardRef(el) {
        this._additionalCardRef = el;
    }
    _setAdditionalContentRef(el) {
        this._additionalContentRef = el;
    }
    _setAdditionalToolbarContentRef(el) {
        this._additionalToolbarContentRef = el;
    }
    _setRoomInputRef(el) {
        this._roomInputRef = el;
    }
    _shouldShowAdditionalCard() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_ADDITIONAL_CARD
            && this._additionalCardTemplate
            && this._additionalCardTemplate.content
            && this._additionalCardTemplate.innerHTML.trim();
    }
    _shouldShowAdditionalContent() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_CONTENT
            && this._additionalContentTemplate
            && this._additionalContentTemplate.content
            && this._additionalContentTemplate.innerHTML.trim();
    }
    _shouldShowAdditionalToolbarContent() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_TOOLBAR_ADDITIONAL_CONTENT
            && this._additionalToolbarContentTemplate
            && this._additionalToolbarContentTemplate.content
            && this._additionalToolbarContentTemplate.innerHTML.trim();
    }
}
WelcomePage.defaultProps = {
    _room: ''
};
export default translate(connect(_mapStateToProps)(WelcomePage));
